import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { productsApiService } from '@/services/api/products'
import type { Product, Category, FilterOptions, ProductSearchMeta } from '@/types'
import { logError } from '@/utils/errors'

type ProductFilters = Partial<FilterOptions> & {
  limit?: number;
};

export const useProductsStore = defineStore('products', () => {
  const CACHE_TTL_MS = 5 * 60 * 1000

  type CachedProductsPayload = {
    data: Product[];
    meta: ProductSearchMeta;
    links?: any;
    cachedAt: number;
  }

  // State
  const products = ref<Product[]>([])
  const categories = ref<Category[]>([])
  const brands = ref<any[]>([])
  const filteredBrands = ref<any[]>([]) // Marcas filtradas por categoría
  const featuredProducts = ref<Product[]>([])
  const currentProduct = ref<Product | null>(null)
  const isLoading = ref(false)
  const isRefreshingProducts = ref(false)
  const isRefreshingProduct = ref(false)
  const error = ref<string | null>(null)
  let categoriesRequest: Promise<void> | null = null
  const productsCache = new Map<string, CachedProductsPayload>()
  const productCache = new Map<string, { product: Product; cachedAt: number }>()
  
  // Pagination
  const currentPage = ref(1)
  const totalPages = ref(1)
  const totalItems = ref(0)
  const itemsPerPage = ref(12)
  const links = ref<any>({})
  const searchMeta = ref<ProductSearchMeta | null>(null)

  const loading = computed(() => isLoading.value)

  const hasNextPage = computed(() => currentPage.value < totalPages.value)
  const hasPrevPage = computed(() => currentPage.value > 1)

  const isCacheFresh = (cachedAt: number) => {
    return Date.now() - cachedAt < CACHE_TTL_MS
  }

  const buildProductsCacheKey = (page: number, filters: ProductFilters) => {
    return JSON.stringify({
      page,
      filters: {
        ...filters,
        categories: [...(filters.categories || [])].sort(),
        brands: [...(filters.brands || [])].sort(),
      },
    })
  }

  const hydrateProductsState = (payload: CachedProductsPayload) => {
    products.value = payload.data
    currentPage.value = Math.min(
      Math.max(payload.meta.current_page, 1),
      Math.max(payload.meta.last_page, 1),
    )
    totalPages.value = payload.meta.last_page
    totalItems.value = payload.meta.total
    itemsPerPage.value = payload.meta.per_page
    links.value = payload.links || {}
    searchMeta.value = payload.meta
  }

  const findCachedProductBySlug = (slug: string): Product | null => {
    const normalizedSlug = String(slug || '').trim()

    if (!normalizedSlug) {
      return null
    }

    const collections = [products.value, featuredProducts.value]

    for (const collection of collections) {
      const match = collection.find(item => item.external_url === normalizedSlug)

      if (match) {
        return match
      }
    }

    return null
  }

  const mergeProductPricing = (freshProduct: Product, cachedProduct?: Product | null): Product => {
    if (!cachedProduct || freshProduct.price_source) {
      return freshProduct
    }

    const hasValidFreshOffer = Boolean(
      freshProduct.offer_price &&
      freshProduct.offer_price < freshProduct.price
    )

    const hasValidCachedOffer = Boolean(
      cachedProduct.offer_price &&
      cachedProduct.offer_price < cachedProduct.price
    )

    if (hasValidFreshOffer || !hasValidCachedOffer) {
      return {
        ...freshProduct,
        product_prices: freshProduct.product_prices?.length ? freshProduct.product_prices : cachedProduct.product_prices,
      }
    }

    return {
      ...freshProduct,
      offer_price: cachedProduct.offer_price,
      original_offer_price: cachedProduct.original_offer_price ?? freshProduct.original_offer_price,
      original_price: cachedProduct.original_price ?? freshProduct.original_price,
      original_currency_id: cachedProduct.original_currency_id ?? freshProduct.original_currency_id,
      original_currency_code: cachedProduct.original_currency_code ?? freshProduct.original_currency_code,
      original_currency_symbol: cachedProduct.original_currency_symbol ?? freshProduct.original_currency_symbol,
      product_prices: cachedProduct.product_prices?.length ? cachedProduct.product_prices : freshProduct.product_prices,
    }
  }

  // Actions
  async function fetchProducts(page = 1, filters: ProductFilters = {}, allowPageRecovery = true) {
    const cacheKey = buildProductsCacheKey(page, filters)
    const cachedResponse = productsCache.get(cacheKey)
    const hasVisibleProducts = products.value.length > 0

    if (cachedResponse && isCacheFresh(cachedResponse.cachedAt)) {
      hydrateProductsState(cachedResponse)
      error.value = null
      isLoading.value = false
      return
    }

    if (hasVisibleProducts) {
      isRefreshingProducts.value = true
    } else {
      isLoading.value = true
    }
    error.value = null
    
    try {
      const response = await productsApiService.getProducts({
        ...filters,
        page,
        limit: filters.limit || itemsPerPage.value,
        categories: filters.categories || [],
        brands: filters.brands || [],
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        inStock: filters.inStock,
        sale: filters.sale,
        featured: filters.featured,
        search: filters.search,
        sortBy: filters.sortBy || 'name',
        sortOrder: filters.sortOrder || 'asc',
      })

      if (
        allowPageRecovery &&
        response.data.length === 0 &&
        response.meta.total > 0 &&
        response.meta.last_page > 0 &&
        response.meta.current_page > response.meta.last_page
      ) {
        return await fetchProducts(response.meta.last_page, filters, false)
      }

      const payload: CachedProductsPayload = {
        data: response.data,
        meta: response.meta,
        links: response.links || {},
        cachedAt: Date.now(),
      }

      productsCache.set(cacheKey, payload)
      hydrateProductsState(payload)
    } catch (err) {
      error.value = 'Failed to fetch products'
      searchMeta.value = null
      logError('Error fetching products', err, { page, filters })
    } finally {
      isLoading.value = false
      isRefreshingProducts.value = false
    }
  }

  async function fetchCategories() {
    if (categories.value.length > 0) {
      return
    }

    if (categoriesRequest) {
      return categoriesRequest
    }

    categoriesRequest = (async () => {
      try {
        const response = await productsApiService.getCategories()
        categories.value = response.categories
        brands.value = response.brands
        filteredBrands.value = [...response.brands] // Inicialmente todas las marcas
      } catch (err) {
        logError('Error fetching categories', err)
      } finally {
        categoriesRequest = null
      }
    })()

    try {
      await categoriesRequest
    } catch {
      // categoriesRequest already handles its own logging
    }
  }

  async function fetchBrandsByCategories(categoryIds: number[]) {
    try {
      const brandsData = await productsApiService.getBrandsByCategories(categoryIds)
      
      // Asegurar que siempre sea un array
      filteredBrands.value = Array.isArray(brandsData) ? [...brandsData] : []
      
      return filteredBrands.value
    } catch (err) {
      logError('Error fetching brands by categories', err, { categoryIds })
      // En caso de error, mostrar todas las marcas
      filteredBrands.value = [...brands.value]
      return filteredBrands.value
    }
  }

  async function fetchFeaturedProducts() {
    try {
      const response = await productsApiService.getFeaturedProducts()
      featuredProducts.value = response.data
    } catch (err) {
      logError('Error fetching featured products', err)
      featuredProducts.value = []
    }
  }

  async function getProduct(id: string) {
    const cachedProduct = productCache.get(id)

    if (cachedProduct && isCacheFresh(cachedProduct.cachedAt)) {
      currentProduct.value = cachedProduct.product
      error.value = null
      isLoading.value = false
      return cachedProduct.product
    }

    if (currentProduct.value) {
      isRefreshingProduct.value = true
    } else {
      isLoading.value = true
    }
    error.value = null
    
    try {
      const product = await productsApiService.getProduct(id)
      currentProduct.value = product
      productCache.set(id, { product, cachedAt: Date.now() })
      return product
    } catch (err) {
      error.value = 'Failed to fetch product details'
      logError('Error fetching product', err, { id })
      currentProduct.value = null
      return null
    } finally {
      isLoading.value = false
      isRefreshingProduct.value = false
    }
  }

  async function loadProducts() {
    return fetchProducts()
  }

  async function loadCategories() {
    return fetchCategories()
  }

  async function loadProduct(slug: string) {
    const cachedProduct = findCachedProductBySlug(slug) || productCache.get(slug)?.product || null

    if (cachedProduct) {
      currentProduct.value = cachedProduct
      error.value = null
      isRefreshingProduct.value = true
    } else {
      isLoading.value = true
      error.value = null
    }

    try {
      const product = await productsApiService.getProductBySlug(slug)
      if (product) {
        currentProduct.value = mergeProductPricing(product, cachedProduct)
        productCache.set(slug, { product: currentProduct.value, cachedAt: Date.now() })
        error.value = null
        return currentProduct.value
      } else {
        error.value = 'Product not found'
        currentProduct.value = null
        return null
      }
    } catch (err) {
      error.value = 'Failed to fetch product details'
      logError('Error fetching product by slug', err, { slug })
      currentProduct.value = null
      return null
    } finally {
      isLoading.value = false
      isRefreshingProduct.value = false
    }
  }

  return {
    // State
    products,
    categories,
    brands,
    filteredBrands,
    featuredProducts,
    currentProduct,
    isLoading,
    isRefreshingProducts,
    isRefreshingProduct,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    links,
    searchMeta,
    
    // Getters
    hasNextPage,
    hasPrevPage,
    
    // Actions
    fetchProducts,
    fetchCategories,
    fetchBrandsByCategories,
    fetchFeaturedProducts,
    getProduct,
    loadProducts,
    loadCategories,
    loadProduct
  }
})
